import React, { useEffect, useRef } from "react";
import { Card, EmptyState, IconButton } from "stories/Admin";
import styles from "./AiChat.module.scss";

/** Shared chat layout for «AI-imam» and «Test av prompter». */

export interface ChatBubble {
  fromUser: boolean;
  content: string;
  timestamp: Date;
  /** Badges etc. under the answer. */
  meta?: React.ReactNode;
}

interface AiChatProps {
  messages: ChatBubble[];
  loading: boolean;
  input: string;
  onInput: (value: string) => void;
  onSend: (e: React.FormEvent) => void;
  placeholder: string;
  emptyTitle: string;
  emptyText: string;
  className?: string;
}

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
  </svg>
);

const AiChat: React.FC<AiChatProps> = ({
  messages,
  loading,
  input,
  onInput,
  onSend,
  placeholder,
  emptyTitle,
  emptyText,
  className,
}) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  return (
    <Card className={`${styles.chat} ${className ?? ""}`}>
      <div className={styles.list} aria-live="polite">
        {messages.length === 0 && !loading ? (
          <EmptyState icon="spark" title={emptyTitle}>
            {emptyText}
          </EmptyState>
        ) : (
          messages.map((msg, index) => (
            <div
              key={index}
              className={`${styles.row} ${msg.fromUser ? styles.rowUser : ""}`}
            >
              <div className={styles.msg}>
                <div className={styles.bubble}>{msg.content}</div>
                {msg.meta}
                <span className={styles.time}>
                  {msg.timestamp.toLocaleTimeString("nb-NO", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className={styles.row}>
            <div className={styles.msg}>
              <div
                className={`${styles.bubble} ${styles.dots}`}
                aria-label="Svarer …"
              >
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={onSend} className={styles.inputRow}>
        <input
          type="text"
          aria-label="Spørsmål"
          placeholder={placeholder}
          value={input}
          onChange={(e) => onInput(e.target.value)}
          autoFocus
        />
        <IconButton
          type="submit"
          variant="primary"
          label="Send"
          icon={<SendIcon />}
          disabled={!input.trim() || loading}
        />
      </form>
    </Card>
  );
};

export default AiChat;
